import AiAssistantIconFilled from "@shared/svg/ai-assistant-active.svg?react";
import AiAssistantIcon from "@shared/svg/ai-assistant.svg?react";
import AIKnowledgeBaseIconFilled from "@shared/svg/ai-knowledgebase-active.svg?react";
import AIKnowledgeBaseIcon from "@shared/svg/ai-knowledgebase.svg?react";
import AiNotetakerIconFilled from "@shared/svg/ai-notetaker-active.svg?react";
import AiNotetakerIcon from "@shared/svg/ai-notetaker.svg?react";
import AISearchIconFilled from "@shared/svg/ai-search-active.svg?react";
import AISearchIcon from "@shared/svg/ai-search.svg?react";
import AnalyticsIconFilled from "@shared/svg/analytics-sub-menu-active.svg?react";
import AnalyticsIcon from "@shared/svg/analytics-sub-menu.svg?react";
import ArchiveWorkOrderIcon from "@shared/svg/archive-work-order.svg?react";
import CalendarBlankFilled from "@shared/svg/calendar-blank-filled.svg?react";
import CalendarBlank from "@shared/svg/calendar-blank.svg?react";
import ConnectionRequestsIconFilled from "@shared/svg/connection-requests-active.svg?react";
import ConnectionRequestsIcon from "@shared/svg/connection-requests.svg?react";
import DocumentIconFilled from "@shared/svg/documents-filled.svg?react";
import DocumentIcon from "@shared/svg/documents.svg?react";
import EmailIconFilled from "@shared/svg/email-filled.svg?react";
import EmailIcon from "@shared/svg/email.svg?react";
import FacilityIconFilled from "@shared/svg/facilities-sub-menu-active.svg?react";
import FacilityIcon from "@shared/svg/facilities-sub-menu.svg?react";
import FolderIconFilled from "@shared/svg/folder-filled.svg?react";
import FolderIcon from "@shared/svg/folder.svg?react";
import HourGlassIconFilled from "@shared/svg/hour-glass-active.svg?react";
import HourGlassIcon from "@shared/svg/hour-glass.svg?react";
import InventoryIconFilled from "@shared/svg/inventory-filled.svg?react";
import InventoryIcon from "@shared/svg/inventory.svg?react";
import MachineTemplatesIconFilled from "@shared/svg/machine-templates-active.svg?react";
import MachineTemplatesIcon from "@shared/svg/machine-templates.svg?react";
import MachineIconFilled from "@shared/svg/machines-filled.svg?react";
import MachineIcon from "@shared/svg/machines.svg?react";
import MyAssistantIconFilled from "@shared/svg/my-assistants-filled.svg?react";
import MyAssistantIcon from "@shared/svg/my-assistants.svg?react";
import MyPortalIconFilled from "@shared/svg/my-portals-active.svg?react";
import MyPortalIcon from "@shared/svg/my-portals.svg?react";
import ProcedureIconFilled from "@shared/svg/procedures-filled.svg?react";
import ProcedureIcon from "@shared/svg/procedures.svg?react";
import ProductsIconFilled from "@shared/svg/products-active.svg?react";
import ProductsIcon from "@shared/svg/products.svg?react";
import QuotesInvoicesIconFilled from "@shared/svg/navigation/quotes-invoices-active.svg?react";
import QuotesInvoicesIcon from "@shared/svg/navigation/quotes-invoices.svg?react";
import InvoicesIconFilled from "@shared/svg/navigation/invoices-active.svg?react";
import InvoicesIcon from "@shared/svg/navigation/invoices.svg?react";
import RequestsIconFilled from "@shared/svg/requests-active.svg?react";
import RequestsIcon from "@shared/svg/requests.svg?react";
import SharedAssetsIconFilled from "@shared/svg/shared-assets-active.svg?react";
import SharedAssetsIcon from "@shared/svg/shared-assets.svg?react";
import { getAllRoles } from "@shared/utils/getAllRoles";
import { useIntl } from "react-intl";

import APP_FEATURES from "$/settings/app-features.json";
import PAID_FEATURES from "$/settings/paid-features.json";
import { isOemAdmin, isStaff } from "~/hooks/_useRoleManager";
import { AppConfigType } from "~/types/appConfig";
import type { SubRouteType } from "~/types/route";
import type { UserType } from "~/types/user";
import { getEnums } from "~/utils";
import { useGetAppConfigValue } from "~/utils/appFeatures";
import isPaidFeatureAvailable from "~/utils/isPaidFeatureAvailable";
import { COMPANY_BASE_PATH } from "@shared/constants/nomenclature";
import useNomenclature from "~/hooks/useNomenclature";

const paidFeatures = getEnums(PAID_FEATURES, "reference");
const appFeaturesEnum = getEnums(APP_FEATURES, "reference");

const ALL_ROLES = getAllRoles({
  getOEM: true,
  getCRM: false,
  getFacility: false,
});

export const ROUTE_TO_KEY_MAP: { [key: string]: string } = {
  "/app/work/work-orders": "cmms",
  "/app/assets": "machines",
  "/app/asset-hub": "asset-hub",
  "/app/ai": "industrial-ai",
  "/app/customer-portal/my-portals": "customerPortal",
};

export const ROUTE_KEY_TO_NOTIFICATION_MAP: { [key: string]: string[] } = {
  cmms: ["openTickets", "myTickets"],
  assetHub: ["requests"],
};

export const SUB_ROUTES: {
  [key: string]: {
    header: string;
    enableIf: (appFeatures: string[]) => boolean;
    routes: SubRouteType[][];
  };
} = {
  "/app/work/work-orders": {
    enableIf: () => true,
    header: "",
    routes: [
      [
        {
          route: "/app/work/work-orders",
          icon: FolderIcon,
          enableIf: () => true,
          iconActive: FolderIconFilled,
          titleKey: "WORK_ORDERS",
          routeKey: "openTickets",
          title: "Work Orders",
        },
        {
          route: "/app/work/my-work-orders",
          icon: DocumentIcon,
          enableIf: () => true,
          iconActive: DocumentIconFilled,
          titleKey: "MY_WORK_ORDERS",
          routeKey: "myTickets",
          title: "My Work Orders",
        },
        {
          route: "/app/work/emails",
          icon: EmailIcon,
          // Visible to all roles (technicians included — they get a personal
          // inbox), gated only on the OEM's email feature.
          enableIf: (user: UserType, _, appConfig: AppConfigType) =>
            isPaidFeatureAvailable(paidFeatures.emails, user, appConfig),
          iconActive: EmailIconFilled,
          titleKey: "EMAILS",
          title: "Emails",
        },
        {
          route: "/app/work/planned-events",
          icon: HourGlassIcon,
          enableIf: () => true,
          iconActive: HourGlassIconFilled,
          titleKey: "PLANNED_EVENTS",
          routeKey: "myPlannedEvents",
          title: "Planned Events",
        },
        {
          route: "/app/work/procedures",
          icon: ProcedureIcon,
          enableIf: () => true,
          iconActive: ProcedureIconFilled,
          titleKey: "PROCEDURES",
          title: "Procedures",
        },
      ],
      [
        {
          route: "/app/work/scheduler",
          icon: CalendarBlank,
          enableIf: (user: UserType) =>
            user.role !== ALL_ROLES.TECHNICIAN.VALUE,
          iconActive: CalendarBlankFilled,
          titleKey: "SCHEDULING",
          title: "Scheduler",
        },
        {
          route: "/app/work/insights",
          icon: AnalyticsIcon,
          enableIf: (user: UserType) => isOemAdmin(user?.role),
          iconActive: AnalyticsIconFilled,
          titleKey: "ANALYTICS",
          title: "Insights",
        },
        {
          route: "/app/work/archived-work-orders",
          icon: ArchiveWorkOrderIcon,
          enableIf: () => true,
          iconActive: ArchiveWorkOrderIcon,
          titleKey: "ARCHIVED_WORK_ORDERS",
          routeKey: "archivedWorkOrders",
          title: "Archive",
        },
      ],
    ],
  },
  "/app/asset-hub": {
    header: "",
    enableIf: () => true,
    routes: [
      [
        {
          route: "/app/asset-hub/my-assets",
          icon: MachineIcon,
          enableIf: () => true,
          iconActive: MachineIconFilled,
          titleKey: "OWN_ASSETS",
          title: "My Assets",
        },
        {
          route: "/app/asset-hub/my-products",
          icon: ProductsIcon,
          enableIf: (_: UserType, appFeatures: string[]) =>
            appFeatures.includes(appFeaturesEnum.productCatalog),
          iconActive: ProductsIconFilled,
          titleKey: "MY_PRODUCT",
          title: "My Products",
        },
        {
          route: "/app/asset-hub/asset-templates",
          icon: MachineTemplatesIcon,
          enableIf: (_: UserType, appFeatures: string[]) =>
            !appFeatures.includes(appFeaturesEnum.productCatalog),
          iconActive: MachineTemplatesIconFilled,
          titleKey: "ASSET_TEMPLATES",
          title: "Asset Templates",
        },
        {
          route: "/app/asset-hub/inventory",
          icon: InventoryIcon,
          enableIf: () => true,
          iconActive: InventoryIconFilled,
          titleKey: "INVENTORY",
          title: "Inventory",
        },
        {
          route: "/app/asset-hub/companies",
          icon: FacilityIcon,
          enableIf: () => true,
          iconActive: FacilityIconFilled,
          titleKey: "CONNECTIONS",
          title: "Connections",
        },
      ],
      [
        {
          route: "/app/asset-hub/shared",
          icon: SharedAssetsIcon,
          enableIf: () => true,
          iconActive: SharedAssetsIconFilled,
          titleKey: "SHARED_WITH_ME",
          title: "Shared with me",
        },
        {
          route: "/app/asset-hub/requests",
          icon: RequestsIcon,
          enableIf: () => true,
          iconActive: RequestsIconFilled,
          titleKey: "REQUESTS",
          title: "Requests",
          routeKey: "requests",
        },
        {
          route: "/app/asset-hub/connection-requests",
          icon: ConnectionRequestsIcon,
          enableIf: () => true,
          iconActive: ConnectionRequestsIconFilled,
          titleKey: "CONNECTION_REQUESTS",
          title: "Connection Requests",
        },
      ],
    ],
  },
  "/app/ai": {
    header: "",
    enableIf: () => true,
    routes: [
      [
        {
          route: "/app/ai/chat",
          icon: AiAssistantIcon,
          enableIf: () => true,
          iconActive: AiAssistantIconFilled,
          titleKey: "CHATS",
          title: "Chats",
        },
        {
          route: "/app/ai/assistant",
          icon: MyAssistantIcon,
          enableIf: () => true,
          iconActive: MyAssistantIconFilled,
          titleKey: "MY_ASSISTANTS",
          title: "My Assistants",
        },
        {
          route: "/app/ai/shared-assistants",
          icon: SharedAssetsIcon,
          enableIf: () => true,
          iconActive: SharedAssetsIconFilled,
          titleKey: "SHARED_ASSISTANTS",
          title: "Shared Assistants",
        },
      ],
      [
        {
          route: "/app/ai/search",
          icon: AISearchIcon,
          enableIf: (user: UserType, _, appConfig: AppConfigType) =>
            isPaidFeatureAvailable(paidFeatures.aiSearch, user, appConfig),
          iconActive: AISearchIconFilled,
          titleKey: "AI_SEARCH",
          title: "AI Search",
        },
        {
          route: "/app/ai/knowledge-base",
          icon: AIKnowledgeBaseIcon,
          enableIf: (user: UserType, _, appConfig: AppConfigType) =>
            isPaidFeatureAvailable(paidFeatures.aiSearch, user, appConfig),
          iconActive: AIKnowledgeBaseIconFilled,
          titleKey: "KNOWLEDGE_BASE",
          title: "Knowledge Base",
        },
      ],
      [
        {
          route: "/app/ai/notetaker",
          icon: AiNotetakerIcon,
          enableIf: () => true,
          iconActive: AiNotetakerIconFilled,
          titleKey: "AI_NOTETAKER",
          title: "AI Notetaker",
        },
      ],
    ],
  },
  "/app/customer-portal/my-portals": {
    header: "",
    enableIf: () => true,
    routes: [
      [
        {
          route: "/app/customer-portal/my-portals",
          icon: MyPortalIcon,
          enableIf: () => true,
          iconActive: MyPortalIconFilled,
          titleKey: "MY_PORTALS",
          title: "My Portals",
        },
        {
          route: "/app/customer-portal/shared-products",
          icon: ProductsIcon,
          enableIf: () => true,
          iconActive: ProductsIconFilled,
          titleKey: "SHARED_PRODUCTS",
          title: "Shared Products",
        },
        {
          route: "/app/work/quotes",
          icon: QuotesInvoicesIcon,
          enableIf: (user: UserType, _: string[], appConfig: AppConfigType) =>
            (isOemAdmin(user?.role) || isStaff(user?.role)) &&
            isPaidFeatureAvailable(
              paidFeatures.quotesAndInvoices,
              user,
              appConfig,
            ),
          iconActive: QuotesInvoicesIconFilled,
          titleKey: "QUOTES",
          title: "Quotes",
        },
        {
          route: "/app/work/invoices",
          icon: InvoicesIcon,
          enableIf: (user: UserType, _: string[], appConfig: AppConfigType) =>
            (isOemAdmin(user?.role) || isStaff(user?.role)) &&
            isPaidFeatureAvailable(
              paidFeatures.quotesAndInvoices,
              user,
              appConfig,
            ),
          iconActive: InvoicesIconFilled,
          titleKey: "INVOICES",
          title: "Invoices",
        },
      ],
    ],
  },
};

export const routeHasSubRoutes = (routeKey: string) => routeKey in SUB_ROUTES;

export const useSubRoutes = (
  routeKey: string,
  user?: UserType,
  appFeatures?: string[],
) => {
  const { messages } = useIntl();
  const { basePath: companyBasePath } = useNomenclature();
  const appConfig = useGetAppConfigValue();

  const subRoutes = SUB_ROUTES[routeKey];
  if (subRoutes) {
    const subMenus = messages?.menus?.[routeKey]?.SUB_MENUS;
    return {
      ...subRoutes,
      routes: subRoutes.routes
        .map((subRouteSection: SubRouteType[]) =>
          subRouteSection.map((subRoute) => ({
            ...subRoute,
            title: subMenus?.[subRoute.titleKey] || subRoute.title,
            // CMMS accounts browse the Company module under /locations.
            route:
              subRoute.route === COMPANY_BASE_PATH
                ? companyBasePath
                : subRoute.route,
          })),
        )
        .map((subRouteSection) =>
          subRouteSection.filter((subRoute) =>
            subRoute.enableIf(user, appFeatures, appConfig),
          ),
        )
        .filter((subRouteSection) => !!subRouteSection.length),
    };
  }
  return subRoutes;
};
